import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import FilterBar from '../../components/FilterBar';
import SingleSelect from '../../components/SingleSelect';
import ReportTile from '../../components/reports/ReportTile';
import TabBar from '../../components/TabBar';
import api from '../../services/api';
import { config } from '../../config/config';
import FuelNoIncome from './FuelNoIncome';
import styles from './OosDeferments.module.css';

interface BalanceItem {
  recno: number;
  type: string;
  total_amount: string;
  paid_amount: string;
  balance: string;
  source_recno?: number;
  invoice_date?: string | null;
  status?: string;
}

interface OosDriver {
  unitnumber: string;
  driver_recno: number;
  driver_name: string;
  region: string;
  dispatcher: string | null;
  companyrep: string | null;
  oos_date: string;
  days_oos: number;
  oos_reason: string | null;
  oos_comments: string | null;
  deferred_balance: string;
  loan_balance: string;
  escrow_balance: string;
  deferments: BalanceItem[];
  loans: BalanceItem[];
}

interface Dashboard { as_of: string; drivers: OosDriver[] }
const dollars = (value: string | number) => Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const dateLabel = (value?: string | null) => {
  if (!value || value.startsWith('0000')) return '—';
  const [year, month, day] = value.slice(0, 10).split('-');
  return `${month}/${day}/${year}`;
};

function ItemTable({ items, deferred, asOf }: { items: BalanceItem[]; deferred: boolean; asOf: string }) {
  if (!items.length) return <p className={styles.emptyDetail}>No outstanding {deferred ? 'deferments' : 'loan or deduction balances'}.</p>;
  return <div className={styles.scroll}><table className={styles.detailTable}>
    <thead><tr><th>{deferred ? 'Invoice date' : 'Status'}</th><th>Source record</th><th>Type</th><th>Original amount</th><th>Paid</th><th>Balance</th></tr></thead>
    <tbody>{items.map(item => <tr key={item.recno}>
      <td>{deferred ? <>{dateLabel(item.invoice_date)}{item.invoice_date && item.invoice_date > asOf && <span className={styles.future}>Upcoming</span>}</> : item.status}</td>
      <td>{deferred ? item.source_recno : item.recno}</td><td>{item.type}</td>
      <td>{dollars(item.total_amount)}</td><td>{dollars(item.paid_amount)}</td><td>{dollars(item.balance)}</td>
    </tr>)}</tbody>
  </table></div>;
}

function OosBalances() {
  const [report, setReport] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [region, setRegion] = useState('');
  const [age, setAge] = useState('15');
  const [balancesOnly, setBalancesOnly] = useState(false);

  const refresh = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get<Dashboard>('/oos-deferments', { signal });
      if (!signal?.aborted) setReport(data);
    } catch {
      if (!signal?.aborted) {
        setReport(null);
        setError('Unable to load OOS balances. Please retry or check your access with an administrator.');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  const regions = useMemo(() => [...new Set(report?.drivers.map(d => d.region) ?? [])].sort(), [report]);
  const visible = useMemo(() => {
    return (report?.drivers ?? []).filter(d =>
      (!region || d.region === region) && d.days_oos >= Number(age) &&
      (!balancesOnly || Number(d.deferred_balance) > 0 || Number(d.loan_balance) > 0)
    );
  }, [report, region, age, balancesOnly]);
  const sum = (key: 'deferred_balance' | 'loan_balance' | 'escrow_balance') =>
    visible.reduce((total, d) => total + Math.round(Number(d[key]) * 100), 0) / 100;
  const columns = useMemo<ColumnDef<OosDriver>[]>(() => [
    { accessorKey: 'unitnumber', header: 'Driver', size: 100, cell: ({ getValue }) => {
      const driver = getValue<string>();
      const unit = parseInt(driver, 10);
      if (!driver || Number.isNaN(unit)) return driver || '—';
      return <a href={`${config.V2_URL}/safety/drivers/${unit}/driver`}
        target="_blank" rel="noopener noreferrer" title="View in CMS" onClick={event => event.stopPropagation()}
        className="font-semibold text-blue-600 hover:underline dark:text-blue-400">{driver}</a>;
    } },
    { accessorKey: 'driver_name', header: 'Name', size: 190 },
    { accessorKey: 'region', header: 'Region', size: 150 },
    { accessorKey: 'days_oos', header: 'Days OOS', size: 105, cell: ({ getValue }) => <span className={styles.days}>{getValue<number>()}</span> },
    { accessorKey: 'oos_date', header: 'OOS since', size: 125, cell: ({ getValue }) => dateLabel(getValue<string>()) },
    { accessorKey: 'oos_reason', header: 'Reason', size: 165 },
    { id: 'deferred_balance', accessorFn: d => Number(d.deferred_balance), header: 'Deferments', size: 130, cell: ({ getValue }) => dollars(getValue<number>()) },
    { id: 'loan_balance', accessorFn: d => Number(d.loan_balance), header: 'Loans / deductions', size: 160, cell: ({ getValue }) => dollars(getValue<number>()) },
    { id: 'escrow_balance', accessorFn: d => Number(d.escrow_balance), header: 'Escrow', size: 130, cell: ({ getValue }) => dollars(getValue<number>()) },
    { accessorKey: 'dispatcher', header: 'Dispatcher', size: 130 },
  ], []);

  return <>
    <header className="shrink-0 flex items-start justify-between gap-4 flex-wrap">
      <div><h1 className="text-xl font-semibold">OOS Deferments</h1><p className="text-sm text-gray-500 dark:text-gray-400">Drivers out of service for more than 14 days, with deferments, loan balances, and escrow.</p></div>
      <button type="button" className={styles.refresh} onClick={() => void refresh()} disabled={loading}><ArrowPathIcon width={16} aria-hidden="true" />{loading ? 'Loading…' : 'Refresh'}</button>
    </header>
    {error && <div role="alert" className={styles.error}>{error}</div>}
    <FilterBar wrap label="" className="shrink-0">
      <FilterBar.Group label="Region">
        <SingleSelect ariaLabel="Region" value={region} onChange={setRegion}
          options={[{ value: '', label: 'All regions' }, ...regions.map(r => ({ value: r, label: r }))]}
          size="sm" className="w-44" enableSearch usePortal />
      </FilterBar.Group>
      <FilterBar.Group label="Time out of service">
        <SingleSelect ariaLabel="Time out of service" value={age} onChange={setAge}
          options={[{ value: '15', label: 'More than 14 days' }, { value: '30', label: '30+ days' }, { value: '60', label: '60+ days' }, { value: '90', label: '90+ days' }]}
          size="sm" className="w-48" usePortal />
      </FilterBar.Group>
      <button type="button" role="switch" aria-checked={balancesOnly} className={styles.balanceToggle} onClick={() => setBalancesOnly(value => !value)}>
        <span className={styles.toggleTrack} aria-hidden="true"><span /></span>
        With outstanding balances only
      </button>
    </FilterBar>
    <section className="shrink-0 grid grid-cols-2 lg:grid-cols-4 gap-2" aria-label="Totals for filtered drivers">
      <ReportTile label="Drivers to review" value={loading || !report ? '—' : visible.length.toLocaleString()} tone="blue" help="Out-of-service drivers matching the current filters." />
      <ReportTile label="Outstanding deferments" value={loading || !report ? '—' : dollars(sum('deferred_balance'))} tone="amber" help="Unpaid recorded deferments for the filtered drivers, including upcoming invoice dates." />
      <ReportTile label="Loan / deduction balances" value={loading || !report ? '—' : dollars(sum('loan_balance'))} tone="purple" help="Remaining open and held loan balances. These may overlap with deferments and are not added to them." />
      <ReportTile label="Escrow balance" value={loading || !report ? '—' : dollars(sum('escrow_balance'))} tone="teal" help="Escrow payment balance for the filtered drivers, shown separately from obligations." />
    </section>
    <p className="shrink-0 text-xs text-gray-500 dark:text-gray-400">Loan balances and deferments may overlap; they are not added together. Escrow is shown separately. Expand a driver to review the individual items.</p>
    <div className="flex-1 min-h-0">
    <TanTable<OosDriver> data={visible} columns={columns} settingsKey="safety-oos-deferments" fieldName="safety-oos-deferments"
      cardTitle="Out-of-service drivers" loading={loading} enableSearch enableColumnFilters={false}
      enableExcelExport enableShowHideCols enableRowsPerPage enableZebraStriping disableVirtualization fillContainer
      getRowId={d => d.unitnumber}
      renderExpandedContent={d => <section className={styles.details} aria-label={`Driver ${d.unitnumber} balances`}>
        <div className={styles.context}><strong>OUT OF SERVICE · {d.days_oos} days</strong><span>Company rep: {d.companyrep || 'Unassigned'}</span><span>Dispatcher: {d.dispatcher || 'Unassigned'}</span></div>
        {d.oos_comments && <p className={styles.comments}>{d.oos_comments}</p>}
        <h2>Outstanding deferments <span>{d.deferments.length} items · {dollars(d.deferred_balance)}</span></h2>
        <ItemTable items={d.deferments} deferred asOf={report?.as_of ?? ''} />
        <h2>Loan / deduction balances <span>{d.loans.length} items · {dollars(d.loan_balance)}</span></h2>
        <ItemTable items={d.loans} deferred={false} asOf={report?.as_of ?? ''} />
      </section>} />
    </div>
    {!loading && report && visible.length === 0 && <p role="status" className={styles.emptyDetail}>No out-of-service drivers match these filters.</p>}
    <details className={styles.definitions}><summary>About these balances{report && ` · As of ${dateLabel(report.as_of)}`}</summary>
      <p>Only current OUT OF SERVICE units with a valid OOS date more than 14 days ago are included. Dates use Central time. In-service and terminated units, mule drivers, partner carriers, GWII W2 employee drivers, and temp labor drivers are excluded.</p>
      <p>Deferments are recorded DEFER items minus PAY/EXPORTED payments. Existing upcoming invoice dates are included and marked Upcoming. Paid-off and DO NOT PAY items are excluded.</p>
      <p>Loans / deductions are remaining OPEN or HOLD loan balances, including handling fees. These are whole balances, not this week’s scheduled deductions. As in the Safety settlement summary, child support, IRS, supplemental insurance, truck rental, IRP IFTA, and IRP plate rental are excluded from this balance.</p>
      <p>Escrow follows the Safety settlement summary: escrow payment amounts excluding DO NOT PAY. Unrecorded future charges are not projected.</p>
    </details>
  </>;
}

/**
 * Two views of the same question — money a driver owes us that we may not get
 * back. Deferments are the balance already stuck on an out-of-service driver;
 * Fuel / No Income is the one still forming, where fuel is going out this week
 * against pay that is not coming in.
 *
 * Each panel mounts only when its tab is opened, so the page loads one query
 * rather than both and the second is paid for only by whoever looks.
 */
export default function OosDeferments() {
  const [tab, setTab] = useState<'oos' | 'fuel'>('oos');
  return <main className="p-4 flex flex-col gap-3 h-[calc(100vh-7rem)] min-h-[450px] overflow-hidden text-gray-900 dark:text-gray-100">
    <TabBar compact noBorder className="shrink-0" onTabChange={id => setTab(id as 'oos' | 'fuel')}
      tabs={[{ id: 'oos', label: 'OOS Deferments', current: tab === 'oos' },
             { id: 'fuel', label: 'Fuel / No Income', current: tab === 'fuel' }]} />
    {tab === 'oos' ? <OosBalances /> : <FuelNoIncome />}
  </main>;
}
